/**
 * Freshness helpers for JSON-LD `dateModified`.
 *
 * Answer engines weight recency on local and transactional queries, so
 * entity pages should publish when their data last changed. The
 * complication is that a single page is fed by several independent
 * update cycles — open-data ingestion, LLM summary regeneration,
 * review approval — each with its own timestamp. Publishing any one of
 * them understates freshness; publishing the newest reflects the page
 * as a whole.
 */

/**
 * Return the most recent of a set of timestamps as an ISO 8601 string,
 * or `undefined` when none are usable.
 *
 * Accepts anything the callers actually hold — ISO strings, date-only
 * strings, `null`, `undefined` — and silently drops values that don't
 * parse. Returning `undefined` rather than a fallback is deliberate: a
 * missing `dateModified` is neutral, whereas a wrong one actively
 * misleads a crawler about how current our data is.
 */
export function newestTimestamp(candidates: Array<string | null | undefined>): string | undefined {
  let newest: number | undefined;

  for (const candidate of candidates) {
    if (!candidate) continue;
    const parsed = Date.parse(candidate);
    // Date.parse returns NaN for unparseable input.
    if (Number.isNaN(parsed)) continue;
    if (newest === undefined || parsed > newest) newest = parsed;
  }

  if (newest === undefined) return undefined;
  return new Date(newest).toISOString();
}
